// Bible & Bank — gom Character/Location/Prop Bank + tóm tắt Visual Bible (redesign 260709 dọn UI).
// 260922 E2-P3: ba tab theo mockup đã duyệt (Nhân vật · Bối cảnh · Đạo cụ) + dòng "Chuẩn bị x/3 — còn thiếu…" kiểu TopView.
import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Card, Descriptions, Tag, Typography, Empty, Collapse } from 'antd';
import { useFilmPlan } from './FilmPlanContext';
import FilmReferenceTab from './FilmReferenceTab';
import FilmLocationBank from './FilmLocationBank';
import FilmPropBank from './FilmPropBank';
import FilmWorldSettingPanel from './FilmWorldSettingPanel';
import { type PrepKey } from './bankPrepUtils';
import { useBankVars } from './useBankVars';
import { useBankPrep } from './useBankPrep';
import './bank.css';

const { Text } = Typography;

interface Props {
  /** Mở thẳng vào một tab con — để nút ở stepper dẫn người dùng tới đúng chỗ thay vì thả họ ở tab đầu. */
  initialTab?: string;
}

/** Tên tab cũ ⇒ tab mới: `looks` (Wardrobe) và `scene-looks` (Outfit per scene) nay nằm trên thẻ nhân vật. */
function normalizeTab(t?: string): PrepKey {
  if (t === 'locations' || t === 'locs') return 'locs';
  if (t === 'props') return 'props';
  return 'chars';
}

const TAB_LABEL: Record<PrepKey, string> = { chars: 'Nhân vật', locs: 'Bối cảnh', props: 'Đạo cụ' };

const FilmBankTab: React.FC<Props> = ({ initialTab }) => {
  const { plan, storyId, planId } = useFilmPlan();
  const vars = useBankVars();
  const vb: any = (plan as any)?.visualBible || {};
  const cast: any[] = vb.cast || [];
  const elements: any[] = vb.elements || [];
  const [tab, setTab] = useState<PrepKey>(normalizeTab(initialTab));
  useEffect(() => { setTab(normalizeTab(initialTab)); }, [initialTab]);
  const [prepKey, setPrepKey] = useState(0);
  const onChanged = useCallback(() => setPrepKey((k) => k + 1), []);
  const prep = useBankPrep(storyId, planId, prepKey);
  const groupOf = (k: PrepKey) => prep?.groups.find((g) => g.key === k);

  const label = (k: PrepKey) => {
    const g = groupOf(k);
    return (
      <span>
        {g && <span className={`bk-tabdot ${g.done ? 'ok' : ''}`} />}
        {TAB_LABEL[k]}
        {g && <span className="bk-tabn">{g.total}</span>}
      </span>
    );
  };

  // Dòng chuẩn bị: mỗi cụm "còn thiếu" bấm được ⇒ nhảy sang tab của nó.
  const prepLine = prep ? (
    <div className="bk-prep">
      <span>Chuẩn bị <b>{prep.done}/3</b></span>
      {prep.groups.every((g) => g.done)
        ? <span className="ok">— đủ</span>
        : <>
            <span>— còn thiếu:</span>
            {prep.groups.flatMap((g) => g.missing.map((m) => (
              <span key={`${g.key}-${m}`} className="it" onClick={() => setTab(g.key)}>{m}</span>
            )))}
          </>}
    </div>
  ) : null;

  return (
    <div className="sb-scope" style={vars}>
      {/* Bối cảnh đứng TRÊN Visual Bible: nó ràng buộc mọi thứ bên dưới (set, trang phục, đạo cụ). */}
      <FilmWorldSettingPanel />
      {plan && (vb.genre_tone || cast.length || elements.length) ? (
        <Collapse size="small" style={{ marginBottom: 12 }} items={[{
          key: 'bible',
          label: <Text style={{ fontSize: 13 }}>Visual Bible · Cast ({cast.length}) · Element ({elements.length})</Text>,
          children: (
            <>
              <Descriptions size="small" column={1} bordered style={{ marginBottom: 8 }}>
                {([['genre_tone', 'Genre/Tone'], ['palette', 'Palette'], ['lighting', 'Lighting'], ['film_look', 'Film look']] as [string, string][])
                  .filter(([k]) => vb[k]).map(([k, label]) => <Descriptions.Item key={k} label={label}>{vb[k]}</Descriptions.Item>)}
              </Descriptions>
              <div>{cast.map((c: any, i: number) => <Tag key={i} color="blue">{c.name}{c.role ? ` · ${c.role}` : ''}</Tag>)}</div>
              {elements.length > 0 && <div style={{ marginTop: 6 }}>{elements.map((e: any, i: number) => <Tag key={i}>{e.name || e.id}</Tag>)}</div>}
            </>
          ),
        }]} />
      ) : null}
      {!plan ? <Empty description="Chọn một plan để xem Bank" /> : (
        <>
        {/* 260927 (phiên test luồng): dòng "Chuẩn bị" từng nằm ở `tabBarExtraContent` — drawer hẹp nên nó đè, dồn các
            tab Bối cảnh/Đạo cụ vào "…". Nay đứng riêng một hàng TRÊN thanh tab. */}
        {prepLine && <div style={{ marginBottom: 4 }}>{prepLine}</div>}
        <Tabs size="small" activeKey={tab} onChange={(k) => setTab(k as PrepKey)}
          items={[
            { key: 'chars', label: label('chars'), children: <Card size="small" bordered={false} styles={{ body: { padding: 0 } }}><FilmReferenceTab onChanged={onChanged} /></Card> },
            // 260922 E2-P3: tab `Wardrobe` gỡ — bộ đồ, ảnh ma-nơ-canh, look-asset nay nằm trên thẻ nhân vật.
            { key: 'locs', label: label('locs'), children: <FilmLocationBank onChanged={onChanged} /> },
            { key: 'props', label: label('props'), children: <FilmPropBank onChanged={onChanged} /> },
          ]} />
        </>
      )}
    </div>
  );
};

export default FilmBankTab;
